import { cn } from "@lib/cn"
import { LockKeyhole } from "lucide-react"
import { useTranslation } from "react-i18next"

import { ADAPTIVE_LEVELS } from "@/constants/adaptiveLevels"
import type { AdaptiveLevel } from "@/types"

/**
 * The five adaptive levels, as a five-step ORDINAL ramp (sky → cyan → indigo →
 * violet → fuchsia).
 *
 * Deliberately raw hues, like the identity palette in
 * `constants/avatarTones.ts` and for a sharper reason: the token layer names
 * five ROLES, and mapping an ordinal scale onto them would say "level 5 is
 * danger" when level 5 is the most advanced work the engine writes. The ramp's
 * job is to read as a progression, which needs a hue sequence, not five
 * semantic verdicts.
 *
 * PRD-59 — the token sweep briefly converted level 3 alone to the accent
 * tokens, which left four raw hues with one token in the middle: the ramp
 * stopped being a ramp. Restored. If this ever wants the theme it needs five
 * ordinal tokens of its own, applied to all five steps at once. The level is
 * always also written out next to the badge, so the colour never carries the
 * meaning alone (`accessibility.md` §5).
 */
const TONES: Record<AdaptiveLevel, string> = {
  1: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/50 dark:text-sky-200 dark:ring-sky-800",
  2: "bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-950/50 dark:text-cyan-200 dark:ring-cyan-800",
  3: "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-200 dark:ring-indigo-800",
  4: "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/50 dark:text-violet-200 dark:ring-violet-800",
  5: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200 dark:bg-fuchsia-950/50 dark:text-fuchsia-200 dark:ring-fuchsia-800",
}

export function AdaptiveLevelBadge({
  level,
  pinned = false,
  explain = false,
  className,
}: {
  level: AdaptiveLevel
  pinned?: boolean
  /** Say what a level DOES ("1 es el más guiado…"), for surfaces where the
   *  badge stands alone — the student page. On hover as a title and always
   *  for a screen reader; the touch path is the detail dialog it sits next
   *  to, which opens with the same sentence. */
  explain?: boolean
  className?: string
}) {
  const { t } = useTranslation("adaptive-levels")
  const max = ADAPTIVE_LEVELS.length
  const label = explain
    ? t(pinned ? "level.explain_pinned" : "level.explain", { level, max })
    : t(pinned ? "level.aria_pinned" : "level.aria", { level })
  return (
    <span
      className={cn(
        "inline-flex min-h-7 shrink-0 items-center gap-1 rounded-full px-2.5 text-xs font-semibold ring-1 ring-inset",
        TONES[level],
        className,
      )}
      title={explain ? label : undefined}
    >
      {/* aria-label on a bare span is not reliably announced; the sr-only
          sentence is. */}
      <span aria-hidden>{t("level.short", { level })}</span>
      {pinned ? <LockKeyhole className="h-3 w-3" aria-hidden /> : null}
      <span className="sr-only">{label}</span>
    </span>
  )
}
